import React, { Suspense, lazy, useMemo } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { ThemeProvider } from "./contexts/ThemeContext";
import Auth0ProviderWithHistory from "./auth/Auth0ProviderWithHistory";
import { useAccountService, useOnboardingService } from "./hooks/useAdminServices";
import { OnboardingStatus } from "./services/admin/types/onboarding.types";
import { HotelProvider, useHotel } from "./contexts/HotelContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { UnifiedNotificationProvider } from "./contexts/UnifiedNotificationProvider";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { HotelTimezoneProvider } from "./contexts/HotelTimezoneContext";
import { PageSettingsProvider } from "./contexts/PageSettingsContext";
import { PmsConfigurationProvider } from "./contexts/PmsConfigurationContext";
import { SignalRProvider } from "./providers/signalr/SignalRProvider";
import { HotelHubWithHotelProvider } from "./providers/signalr/HotelHubWithHotelProvider";
import { ReactQueryProvider } from "./providers/ReactQueryProvider";
import { useUnifiedNotificationStore } from "./stores/unifiedNotificationStore";
import { logger } from "./utils/logger";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTheme } from "./contexts/ThemeContext";
import { routeConfig, generateRoutes } from "./config/routes";
import { HotelLoader } from "./components/shared/HotelLoader";
import AppBootstrapOverlay, { BootstrapStage, REGISTRATION_MODE_KEY } from "./components/admin/AppBootstrapOverlay";
import GuestBootstrapOverlay from "./components/guest/GuestBootstrapOverlay";
import { isDemoSubdomain } from "./utils/demoDetection";
import RouteAwareTranslationProvider from "./components/RouteAwareTranslationProvider";
import RegisterPage from "./pages/RegisterPage";
import { useBillingService } from "./hooks/useAdminServices";
import { useAdminBootstrapQuery } from "./hooks/admin/queries/useBootstrapQuery";
import { AdminBootstrapDto } from "./services/admin/types/account.types";

// Core components - always needed, no benefit to lazy loading
import AdminLayout from "./components/admin/AdminLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import { clearAuthSessionMarkers } from "./utils/authUtils";

// Lazy load edge-case pages (first-time setup, invites, onboarding)
// FirstSetupPage is deprecated - hotel creation is now handled inline in NoHotelsPage
const NoHotelsPage = lazy(() => import("./pages/admin/NoHotelsPage"));
const InviteAcceptPage = lazy(() => import("./pages/admin/InviteAcceptPage"));
const OnboardingWizard = lazy(() => import("./components/onboarding/OnboardingWizard"));
const AdminTermsGuard = lazy(() => import("./components/admin/AdminTermsGuard"));
// Lazy load billing pages for onboarding payment redirect handling
const BillingSuccessPage = lazy(() => import("./pages/admin/BillingSuccessPage"));
const BillingCancelPage = lazy(() => import("./pages/admin/BillingCancelPage"));

// Lazy load guest components
const GuestRoutes = lazy(() => import("./components/GuestApp"));

// Lazy load demo lobby
const DemoLobbyPage = lazy(() => import("./pages/demo/DemoLobbyPage"));

// Lazy load public pages (no auth required)
const PublicSchedulePage = lazy(() => import("./pages/public/PublicSchedulePage"));

/**
 * Wrapper component that forces remount of children when hotel changes.
 * Uses a mount key that only increments AFTER a hotel switch completes.
 * This ensures:
 * 1. No remount on initial page load
 * 2. No remount during the switch animation (which would reset progress)
 * 3. Remount only after isSwitching becomes false
 * 4. Notification counts are reset before children remount
 */
const HotelScopedContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { myHotelInfo, isSwitching } = useHotel();
  const resetNotifications = useUnifiedNotificationStore((state) => state.reset);
  const [mountKey, setMountKey] = React.useState(0);
  const prevHotelIdRef = React.useRef<string | null>(null);
  const pendingSwitchRef = React.useRef(false);

  React.useEffect(() => {
    const currentHotelId = myHotelInfo?.hotelId || null;

    // If hotel ID changed and we had a previous one, mark a pending switch
    if (currentHotelId && prevHotelIdRef.current && currentHotelId !== prevHotelIdRef.current) {
      pendingSwitchRef.current = true;
    }

    // Update the previous hotel ID
    prevHotelIdRef.current = currentHotelId;

    // When switching finishes and there's a pending switch, reset notifications and remount
    if (!isSwitching && pendingSwitchRef.current) {
      pendingSwitchRef.current = false;
      resetNotifications();
      setMountKey(k => k + 1);
    }
  }, [myHotelInfo?.hotelId, isSwitching, resetNotifications]);

  return <React.Fragment key={mountKey}>{children}</React.Fragment>;
};

interface AdminRoutesContentProps {
  authIsLoading: boolean;
  bootstrapData?: AdminBootstrapDto;
}

/**
 * Inner component that handles hotel-dependent state and effects.
 * MUST be rendered inside HotelProvider to access hotel context properly.
 */
const AdminRoutesContent: React.FC<AdminRoutesContentProps> = ({ authIsLoading, bootstrapData }) => {
  const { isAuthenticated, user, logout } = useAuth0();
  const { myHotelInfo, isSwitching, isHotelInfoLoading, hasCompletedInitialFetch, fetchError: hotelFetchError, isAuthError: hotelIsAuthError, refreshHotelInfo } = useHotel();
  const [userPermissions, setUserPermissions] = React.useState<string[] | null>(
    null,
  );
  const [permissionsLoading, setPermissionsLoading] =
    React.useState<boolean>(false);
  const accountService = useAccountService();
  const billingService = useBillingService();
  const onboardingService = useOnboardingService();
  const location = useLocation();
  const [subscriptionChecked, setSubscriptionChecked] = React.useState(false);
  const [subscriptionActive, setSubscriptionActive] =
    React.useState<boolean>(true);
  // Full subscription data for the global warning banner (PastDue / grace period info)
  const [subscriptionWarning, setSubscriptionWarning] =
    React.useState<import("./services/admin/types/billing.types").SubscriptionWarningData | null>(null);
  // Derive hotel state from HotelContext instead of making a separate API call.
  // hasCompletedInitialFetch flips false→true exactly once on initial mount
  // and stays true; that's the only signal we need.
  //
  // DO NOT add `&& !isHotelInfoLoading` here. It looks defensive but causes a
  // brutal remount loop: any background refresh (e.g. BillingSuccessPage calling
  // refreshHotelInfo after Stripe redirect) flips isHotelInfoLoading→true
  // → hotelInfoChecked→false → bootstrapStage→"preparing" → AppBootstrapOverlay
  // replaces the routed page → the routed page unmounts, its async handler
  // keeps running, fires toast.success, then remounts fresh → its mount
  // useEffect re-fires → fetch → unmount → ... 12 cycles/sec until rate limits
  // pile up. Observed in prod May 2026 (cascading toasts on /admin/billing/success).
  const hotelInfoChecked = !authIsLoading && hasCompletedInitialFetch;
  const hasHotel = !!myHotelInfo?.hotelId;
  const bootstrapError = hotelFetchError;
  const [onboardingChecked, setOnboardingChecked] = React.useState(false);
  const [onboardingComplete, setOnboardingComplete] = React.useState(true); // Assume complete by default
  // Track recently completed switch to avoid bootstrap overlay flash
  const [recentlySwitched, setRecentlySwitched] = React.useState(false);

  // Track previous path to detect navigation from invite/accept page
  // Uses a ref to avoid triggering re-renders, updated only after checkHotel completes
  const prevPathRef = React.useRef(location.pathname);

  // Track hotel ID changes to reset state when switching hotels
  const prevHotelIdRef = React.useRef<string | undefined>(undefined);
  const wasSwitchingRef = React.useRef(false);

  // Detect when switch completes and set grace period
  React.useEffect(() => {
    if (wasSwitchingRef.current && !isSwitching) {
      // Switch just completed - set grace period flag to avoid bootstrap overlay flash
      // This gives time for subscription/permission checks to complete in background
      setRecentlySwitched(true);
      const timer = setTimeout(() => {
        setRecentlySwitched(false);
      }, 2000); // 2 second grace period for background checks
      return () => clearTimeout(timer);
    }
    wasSwitchingRef.current = isSwitching;
  }, [isSwitching]);

  React.useEffect(() => {
    const currentHotelId = myHotelInfo?.hotelId;
    if (prevHotelIdRef.current && currentHotelId && prevHotelIdRef.current !== currentHotelId) {
      // Hotel changed - reset states that need re-fetching for new hotel
      logger.info(`App: Hotel changed from ${prevHotelIdRef.current} to ${currentHotelId}, resetting state (isSwitching=${isSwitching})`);

      // These always need to be re-checked for the new hotel
      // hotelInfoChecked and hasHotel are now derived from HotelContext
      setSubscriptionChecked(false);
      setSubscriptionActive(true);
      setOnboardingChecked(false);
      setOnboardingComplete(true);
      setUserPermissions(null);
    }
    prevHotelIdRef.current = currentHotelId;
  }, [myHotelInfo?.hotelId, isSwitching]);

  // Retry handler - triggers HotelContext to re-fetch
  const handleRetry = React.useCallback(() => {
    refreshHotelInfo();
  }, [refreshHotelInfo]);

  const handleLogout = React.useCallback(() => {
    clearAuthSessionMarkers();
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  const refreshPermissions = React.useCallback(async () => {
    if (!isAuthenticated || !user || !accountService || !hasHotel) return;
    setPermissionsLoading(true);
    try {
      const result = await accountService.getMyPermissions();
      if (result.success) {
        setUserPermissions(result.data);
      } else {
        logger.error("Failed to fetch user permissions:", result.error);
      }
    } catch (error) {
      logger.error("Failed to refresh permissions:", error);
    } finally {
      setPermissionsLoading(false);
    }
  }, [isAuthenticated, user, accountService, hasHotel]);

  // Use bootstrap data for permissions when available, fall back to individual fetch
  React.useEffect(() => {
    if (bootstrapData?.permissions && bootstrapData.permissions.length > 0) {
      setUserPermissions(bootstrapData.permissions);
      setPermissionsLoading(false);
      return;
    }

    const fetchPermissions = async () => {
      if (isAuthenticated && user && accountService && hotelInfoChecked && hasHotel) {
        setPermissionsLoading(true);
        try {
          const result = await accountService.getMyPermissions();
          if (result.success) {
            setUserPermissions(result.data);
          } else {
            logger.error("Failed to fetch user permissions:", result.error);
            try {
              const boot = await accountService.bootstrapAdmin();
              if (boot.success) {
                const retry = await accountService.getMyPermissions();
                if (retry.success) {
                  setUserPermissions(retry.data);
                }
              }
            } catch (e) {
              logger.error("Bootstrap admin failed:", e);
            }
          }
        } catch (error) {
          logger.error("Failed to fetch user permissions:", error);
        } finally {
          setPermissionsLoading(false);
        }
      }
    };
    fetchPermissions();
  }, [isAuthenticated, user, accountService, hotelInfoChecked, hasHotel, bootstrapData?.permissions]);

  // Use bootstrap data for subscription when available, fall back to individual fetch.
  // Also fetch full subscription data for the global warning banner (grace period info).
  React.useEffect(() => {
    if (bootstrapData && hotelInfoChecked && hasHotel) {
      setSubscriptionActive(bootstrapData.subscriptionActive);
      setSubscriptionChecked(true);
      // Bootstrap only has a boolean — still fetch full sub data for the warning banner
      // (non-blocking, runs in background after bootstrap is done)
      if (billingService) {
        billingService.getCurrentSubscription().then((res) => {
          if (res.success && res.data.subscription) {
            const sub = res.data.subscription;
            if (sub.status === "PastDue" || sub.status === "Suspended") {
              setSubscriptionWarning({
                status: sub.status,
                gracePeriodEndsAt: sub.gracePeriodEndsAt,
                currentPeriodEnd: sub.currentPeriodEnd,
              });
            }
          }
        }).catch((e) => logger.warn("Failed to fetch subscription details for warning banner:", e));
      }
      return;
    }

    const checkSubscription = async () => {
      if (!isAuthenticated || !billingService || !hotelInfoChecked || !hasHotel)
        return;
      try {
        const res = await billingService.getCurrentSubscription();
        if (res.success) {
          const isActive = !!(res.data.hasSubscription && res.data.subscription?.status === "Active");
          setSubscriptionActive(isActive);
          // Populate warning banner data for PastDue / Suspended states
          const sub = res.data.subscription;
          if (sub && (sub.status === "PastDue" || sub.status === "Suspended")) {
            setSubscriptionWarning({
              status: sub.status,
              gracePeriodEndsAt: sub.gracePeriodEndsAt,
              currentPeriodEnd: sub.currentPeriodEnd,
            });
          }
        } else if (res.error?.status === 403) {
          setSubscriptionActive(true);
        } else {
          setSubscriptionActive(false);
        }
      } catch (e) {
        logger.error("Failed to check subscription:", e);
        setSubscriptionActive(true);
      } finally {
        setSubscriptionChecked(true);
      }
    };
    checkSubscription();
  }, [
    isAuthenticated,
    billingService,
    hotelInfoChecked,
    hasHotel,
    myHotelInfo?.hotelId,
    bootstrapData,
  ]);

  // Use bootstrap data for onboarding when available, fall back to individual fetch
  React.useEffect(() => {
    if (bootstrapData && hotelInfoChecked && hasHotel) {
      setOnboardingComplete(bootstrapData.onboardingComplete);
      setOnboardingChecked(true);
      return;
    }

    const checkOnboarding = async () => {
      if (!isAuthenticated || !onboardingService || !hotelInfoChecked || !hasHotel) {
        return;
      }
      try {
        const result = await onboardingService.getStatus();
        if (result.success) {
          setOnboardingComplete(result.data.status === OnboardingStatus.Completed);
        } else {
          setOnboardingComplete(true);
        }
      } catch (e) {
        logger.error("Failed to check onboarding status:", e);
        setOnboardingComplete(true);
      } finally {
        setOnboardingChecked(true);
      }
    };
    checkOnboarding();
  }, [isAuthenticated, onboardingService, hotelInfoChecked, hasHotel, myHotelInfo?.hotelId, bootstrapData]);

  // Calculate unified bootstrap stage
  // Skip bootstrap overlay during hotel switch - HotelSwitchOverlay handles that
  const bootstrapStage: BootstrapStage = useMemo(() => {
    // When switching hotels or just finished switching, HotelSwitchOverlay handles loading
    // Don't show AppBootstrapOverlay which would unmount AdminLayout and cause visual jumps
    // The recentlySwitched grace period allows permission/subscription checks to complete
    if (isSwitching || recentlySwitched) return "complete";
    if (authIsLoading) return "authenticating";
    if (!hotelInfoChecked) return "preparing";
    if (bootstrapError) return "error";
    if (isAuthenticated && hasHotel && permissionsLoading) return "permissions";
    if (hasHotel && !onboardingChecked) return "finalizing";
    return "complete";
  }, [isSwitching, recentlySwitched, authIsLoading, hotelInfoChecked, bootstrapError, isAuthenticated, hasHotel, permissionsLoading, onboardingChecked]);

  const isBootstrapping = bootstrapStage !== "complete";

  // Track path changes for invite/accept detection (must be before early returns to satisfy rules of hooks)
  const isLeavingInviteAccept = prevPathRef.current.startsWith("/admin/invite/accept") &&
    !location.pathname.startsWith("/admin/invite/accept");
  React.useEffect(() => {
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  // If unpaid, force redirect to billing for any non-essential paths
  // Allow: billing, settings, invoices, account settings, and support
  const restrictToBilling =
    isAuthenticated && subscriptionChecked && !subscriptionActive;
  const isBillingPath = location.pathname.startsWith("/admin/billing");
  const isSettingsPath = location.pathname.startsWith("/admin/settings");
  const isInvoicesPath = location.pathname.startsWith("/admin/invoices");
  const isAccountPath = location.pathname.startsWith("/admin/account");
  const isSupportPath = location.pathname.startsWith("/admin/support");

  // Check if we're in registration mode for dark overlay
  const usesDarkOverlay = isRegistrationMode();

  // Allow unauthenticated users to see NoHotelsPage without bootstrap overlay
  // This provides a nice landing page before they sign in
  const isNoHotelsPath = location.pathname.startsWith("/admin/no-hotels");
  if (!isAuthenticated && !authIsLoading && isNoHotelsPath) {
    return (
      <RouteAwareTranslationProvider>
        <Routes>
          <Route path="/admin/no-hotels" element={<NoHotelsPage />} />
        </Routes>
      </RouteAwareTranslationProvider>
    );
  }

  // Show unified bootstrap overlay during loading phases (including error state)
  if (isBootstrapping) {
    return (
      <RouteAwareTranslationProvider>
        <AppBootstrapOverlay
          stage={bootstrapStage}
          isComplete={false}
          errorMessage={bootstrapError || undefined}
          onRetry={bootstrapStage === "error" ? handleRetry : undefined}
          onLogout={bootstrapStage === "error" && hotelIsAuthError ? handleLogout : undefined}
          variant={usesDarkOverlay ? "dark" : "light"}
        />
      </RouteAwareTranslationProvider>
    );
  }

  return (
    <RouteAwareTranslationProvider>
      {!hasHotel ? (
        // User has no hotels - show NoHotelsPage (except for invite/accept flow)
        location.pathname.startsWith("/admin/invite/accept") ? (
          <Routes>
            <Route path="/admin/invite/accept" element={<InviteAcceptPage />} />
          </Routes>
        ) : location.pathname.startsWith("/admin/no-hotels") ? (
          <Routes>
            <Route path="/admin/no-hotels" element={<NoHotelsPage />} />
          </Routes>
        ) : isLeavingInviteAccept ? (
          // User just accepted an invitation and navigated away - show loader while re-checking
          // The checkHotel effect will run and update hasHotel, then this will re-render
          <AppBootstrapOverlay stage="preparing" isComplete={false} variant={usesDarkOverlay ? "dark" : "light"} />
        ) : (
          // Redirect ALL other admin paths (including /admin/first-setup) to no-hotels page
          <Navigate replace to="/admin/no-hotels" />
        )
      ) : !onboardingComplete ? (
        // Show onboarding wizard for incomplete onboarding
        // BUT allow billing success/cancel pages for Stripe redirect handling
        // These pages poll for payment status and then redirect to dashboard
        location.pathname.startsWith("/admin/billing/success") ? (
          <div className="min-h-screen bg-linear-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
            <Suspense fallback={<BootstrapFallback />}>
              <BillingSuccessPage />
            </Suspense>
          </div>
        ) : location.pathname.startsWith("/admin/billing/cancel") ? (
          <div className="min-h-screen bg-linear-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
            <Suspense fallback={<BootstrapFallback />}>
              <BillingCancelPage />
            </Suspense>
          </div>
        ) : (
          <OnboardingWizard />
        )
      ) : restrictToBilling && !isBillingPath && !isSettingsPath && !isInvoicesPath && !isAccountPath && !isSupportPath ? (
        <AdminTermsGuard>
          <Routes>
            <Route
              path="/admin"
              element={<AdminLayout userPermissions={userPermissions ?? []} limitToBilling subscriptionWarning={subscriptionWarning} onRefreshPermissions={refreshPermissions} />}
            >
              <Route index element={<Navigate replace to="/admin/billing" />} />
              <Route path="*" element={<Navigate replace to="/admin/billing" />} />
            </Route>
          </Routes>
        </AdminTermsGuard>
      ) : (
        <AdminTermsGuard>
          <Routes>
            <Route
              path="/admin"
              element={
                <AdminLayout userPermissions={userPermissions ?? []} limitToBilling={restrictToBilling} subscriptionWarning={subscriptionWarning} onRefreshPermissions={refreshPermissions} />
              }
            >
              <Route path="invite/accept" element={<InviteAcceptPage />} />
              {generateRoutes(routeConfig.admin, userPermissions ?? undefined)}
              <Route index element={<Navigate replace to="dashboard" />} />
            </Route>
          </Routes>
        </AdminTermsGuard>
      )}
    </RouteAwareTranslationProvider>
  );
};

// Check if we're in registration mode (dark overlay during registration/onboarding)
const isRegistrationMode = (): boolean => {
  try {
    return localStorage.getItem(REGISTRATION_MODE_KEY) === "true";
  } catch {
    return false;
  }
};

// Minimal fallback that matches AppBootstrapOverlay background for seamless transition
const BootstrapFallback: React.FC = () => {
  const isDark = isRegistrationMode();
  return (
    <div
      className={
        isDark
          ? "fixed inset-0 z-100 bg-linear-to-br from-slate-900 via-slate-800 to-slate-900"
          : "fixed inset-0 z-100 bg-background/95 backdrop-blur-xs"
      }
    />
  );
};

/**
 * Fetches unified bootstrap data and passes it to context providers,
 * eliminating 7+ individual API calls on admin page load.
 * Waits for bootstrap data before mounting context providers so they
 * receive initial data and skip their own individual API calls.
 */
const AdminBootstrapGate: React.FC<{ authIsLoading: boolean }> = ({ authIsLoading }) => {
  const { data: bootstrapData, isLoading: isBootstrapLoading } = useAdminBootstrapQuery();
  const { myHotelInfo, hasCompletedInitialFetch } = useHotel();
  const hasHotel = !!myHotelInfo?.hotelId;

  // Wait for bootstrap data before rendering context providers.
  // This prevents contexts from falling back to individual API calls.
  // Gate cases:
  // - Hotel info not yet fetched → block (don't mount contexts with undefined data)
  // - Has hotel but bootstrap not resolved → block (wait for bootstrap)
  // - No hotel (confirmed) → render contexts immediately (user goes to no-hotels page)
  // - Bootstrap resolved → render contexts with initial data
  const hotelStatusKnown = hasCompletedInitialFetch;
  const shouldWaitForBootstrap = hasHotel && isBootstrapLoading && !bootstrapData;
  if (!hotelStatusKnown || shouldWaitForBootstrap) {
    return (
      <ProtectedRoute>
        <RouteAwareTranslationProvider>
          <AppBootstrapOverlay stage="preparing" isComplete={false} />
        </RouteAwareTranslationProvider>
      </ProtectedRoute>
    );
  }

  return (
    <CurrencyProvider initialSettings={bootstrapData?.currencySettings ?? undefined}>
      <HotelTimezoneProvider initialTimeZone={bootstrapData ? (bootstrapData.timeZone || "UTC") : undefined}>
        <PageSettingsProvider initialDisabledPages={bootstrapData?.disabledPages}>
          <PmsConfigurationProvider initialPmsStatus={bootstrapData?.pmsStatus ?? undefined}>
            <ProtectedRoute>
              <AdminRoutesContent authIsLoading={authIsLoading} bootstrapData={bootstrapData} />
            </ProtectedRoute>
          </PmsConfigurationProvider>
        </PageSettingsProvider>
      </HotelTimezoneProvider>
    </CurrencyProvider>
  );
};

// Admin routes with Auth0 authentication and permissions
const AdminRoutesWithAuth: React.FC = () => {
  const { isLoading: authIsLoading } = useAuth0();

  return (
    <Suspense fallback={<BootstrapFallback />}>
      <HotelProvider>
        <HotelScopedContent>
          <SignalRProvider>
            <HotelHubWithHotelProvider>
                <UnifiedNotificationProvider>
                  <NotificationProvider>
                    <AdminBootstrapGate authIsLoading={authIsLoading} />
                  </NotificationProvider>
                </UnifiedNotificationProvider>
            </HotelHubWithHotelProvider>
          </SignalRProvider>
        </HotelScopedContent>
      </HotelProvider>
    </Suspense>
  );
};

// Main App component that routes to appropriate sub-app
const AppRoutes: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth0();

  // Demo subdomain — show lobby page at root (guest routes still work on /g/*)
  if (isDemoSubdomain() && (location.pathname === "/" || location.pathname === "")) {
    return (
      <Suspense fallback={null}>
        <DemoLobbyPage />
      </Suspense>
    );
  }

  if (location.pathname.startsWith("/admin")) {
    return <AdminRoutesWithAuth />;
  }

  // Registration flow routes outside GuestApp (no guest header)
  if (location.pathname === "/register") {
    if (isLoading) {
      return <HotelLoader message="Checking session" />;
    }
    if (isAuthenticated) {
      return <Navigate replace to="/admin" />;
    }
    return (
      <RouteAwareTranslationProvider>
        <RegisterPage />
      </RouteAwareTranslationProvider>
    );
  }
  // Public shared pages (no auth, no admin/guest chrome)
  if (location.pathname.startsWith("/s/")) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/s/:token" element={<PublicSchedulePage />} />
        </Routes>
      </Suspense>
    );
  }

  // Auth0 callback arrives on root by default with both code+state params.
  // Match the SDK's hasAuthParams: (code || error) && state — both must be present.
  // Let Auth0Provider finish processing the callback before rendering routes.
  const rootParams = new URLSearchParams(location.search);
  if (
    location.pathname === "/" &&
    (rootParams.has("code") || rootParams.has("error")) &&
    rootParams.has("state")
  ) {
    return null;
  }
  // Route root to admin entry; AdminRoutesWithAuth will gate appropriately
  if (location.pathname === "/") {
    return <Navigate replace to="/admin" />;
  }
  // onboarding route removed

  // Route all guest pages under /g
  return (
    <Suspense fallback={<GuestBootstrapOverlay stage="loading" />}>
      <GuestRoutes />
    </Suspense>
  );
};

const ThemedToastContainer: React.FC = () => {
  const { theme } = useTheme();
  return (
    <ToastContainer
      position="top-right"
      autoClose={5000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme={theme}
      style={{ fontSize: "14px" }}
    />
  );
};

function App() {
  return (
    <ReactQueryProvider>
      <ThemeProvider>
        <Router>
          <Auth0ProviderWithHistory>
            <ThemedToastContainer />
            <div className="flex flex-col h-screen">
              <main className="grow flex flex-col min-h-0 overflow-hidden">
                <AppRoutes />
              </main>
            </div>
          </Auth0ProviderWithHistory>
        </Router>
      </ThemeProvider>
    </ReactQueryProvider>
  );
}

export default App;
